import React, { FormEvent, useContext, useState } from "react";
import {
    ModalContainer,
    Header,
    Title,
    CloseButton,
    Form,
    FormField,
    Label,
    Required,
    Input,
    Footer,
    CancelButton,
    CreateButton,
    ErrorMessage,
} from "./AddGatewayModal.styles";
import { ApplicationContext } from "../../../../components/ApplicationProvider";
import closeIconSvg from "../../../../images/close_mark.svg";
import { Notifier } from "../../../../view/Notifier";
import { CloudAccessType } from "../../../../graphql";
import { GatewayRegionSelector } from "../../../Gateway/GatewayRegionSelector";
import { StyledModal } from "../Gateways.styles";
import GatewayPrivacySettings from "./GatewayPrivacySettings/GatewayPrivacySettings";
import { createGatewayWithInstaller } from "../GatewayAction";
import { GROUP_NAME_MAX_LENGTH, validateGatewayForm } from "./GatewayValidation";

const FORM_ID = "add-gateway-form";

type AddGatewayModalProps = {
    open: boolean;
    onClose: () => void;
    setCheckIfPollingTrue: (setProperty: boolean) => void;
    setNewThingName: (setThingName: string) => void;
    onSuccessfullRequest: () => void;
    printerSerial?: string;
    cloudAccessType?: CloudAccessType;
};

const AddGatewayModal: React.FC<AddGatewayModalProps> = ({
    open,
    onClose,
    setCheckIfPollingTrue,
    setNewThingName,
    onSuccessfullRequest,
    printerSerial,
    cloudAccessType,
}) => {
    const { t } = useContext(ApplicationContext);
    const [site, setSite] = useState("");
    const [region, setRegion] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [useDefaultPrivacy, setUseDefaultPrivacy] = useState(true);

    const clearForm = (): void => {
        setSite("");
        setRegion("");
        setError("");
        setIsSubmitting(false);
        setUseDefaultPrivacy(true);
    };

    const handleClose = (): void => {
        clearForm();
        onClose();
    };

    const handlePrivacyToggle = (): void => {
        setUseDefaultPrivacy((previous) => !previous);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        const validationError = validateGatewayForm(site, region, {
            siteNameRequired: t("gateway_modal.validation.site_name_required"),
            siteNameTooLong: t("gateway_modal.validation.site_name_too_long", {
                maxLength: GROUP_NAME_MAX_LENGTH,
            }),
            siteNameSpaceNotAllowed: t("gateway_modal.validation.site_name_space_not_allowed"),
            siteNameInvalidFormat: t("gateway_modal.validation.site_name_invalid_format"),
            regionRequired: t("gateway_modal.validation.region_required"),
        });

        if (validationError) {
            setError(validationError);
            return;
        }

        setError("");
        setIsSubmitting(true);

        try {
            const coreDeviceThingName = await createGatewayWithInstaller({
                siteName: site.trim(),
                region: region.trim(),
                printerSerial,
                cloudAccessType,
            });

            if (!coreDeviceThingName) {
                setIsSubmitting(false);
                return;
            }

            setNewThingName(coreDeviceThingName);

            Notifier.success(t("gateway_modal.success_message"));

            clearForm();
            onSuccessfullRequest();
            setCheckIfPollingTrue(true);
        } catch {
            setIsSubmitting(false);

            Notifier.fail(t("gateways.installer_creation_failed_msg"));
        }
    };

    const handleSiteChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (error) {
            setError("");
        }

        setSite(event.target.value);
    };

    const handleRegionChange = (_event: React.SyntheticEvent<HTMLElement>, value: string): void => {
        if (error) {
            setError("");
        }

        setRegion(value);
    };

    return (
        <StyledModal open={open} onClose={handleClose} closeOnDimmerClick={false}>
            <ModalContainer>
                <Header>
                    <Title>{t("devices.gateways.add_gateway_modal.title")}</Title>
                    <CloseButton
                        type="button"
                        aria-label="Close"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        data-testid="gateway-modal-close-button"
                    >
                        <img src={closeIconSvg} alt="Close" />
                    </CloseButton>
                </Header>

                <Form id={FORM_ID} onSubmit={handleSubmit}>
                    <FormField>
                        <Label htmlFor="site">
                            {t("devices.gateways.add_gateway_modal.site")}
                            <Required>*</Required>
                        </Label>
                        <Input
                            id="site"
                            placeholder="e.g. Austin HQ"
                            value={site}
                            onChange={handleSiteChange}
                            disabled={isSubmitting}
                            data-testid="gateway-modal-site-name-input"
                        />
                    </FormField>
                    <FormField>
                        <GatewayRegionSelector
                            id="region_selector"
                            disabled={isSubmitting}
                            selectedRegion={region}
                            onChange={handleRegionChange}
                            data-testid="gateway-modal-region-selector"
                        />
                    </FormField>
                    <GatewayPrivacySettings
                        enabled={useDefaultPrivacy}
                        disabled={isSubmitting}
                        onToggle={handlePrivacyToggle}
                        data-testid="gateway-modal-privacy-settings"
                    />
                    {error && (
                        <ErrorMessage role="alert" data-testid="gateway-modal-error-message">
                            {error}
                        </ErrorMessage>
                    )}
                </Form>

                <Footer>
                    <CancelButton type="button" onClick={handleClose} disabled={isSubmitting}>
                        {t("devices.cancel")}
                    </CancelButton>
                    <CreateButton
                        type="submit"
                        form={FORM_ID}
                        disabled={isSubmitting}
                        data-testid="gateway-modal-save-button"
                    >
                        {t("devices.gateways.add_gateway_modal.create_gateway")}
                    </CreateButton>
                </Footer>
            </ModalContainer>
        </StyledModal>
    );
};

export default AddGatewayModal;
